// LineRaceChart.jsx
import React, { useEffect, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import { FaChartBar, FaChartLine, FaDownload } from "react-icons/fa";
import { RiResetLeftLine } from "react-icons/ri";

const LineRaceChart = ({
  data,
  title,
  singleValue,
  from,
  textrotate,
  setSelectedLineBar,
  barName,
}) => {
  const rotate = textrotate ? textrotate : textrotate === 0 ? 0 : 40;
  const [chartType, setChartType] = useState("reset");
  const chartRef = useRef(null);
  useEffect(() => {
    if (singleValue) {
      setChartType("line");
    }
  }, [singleValue]);

  const downloadChart = () => {
    const echartsInstance = chartRef.current.getEchartsInstance();
    const imgData = echartsInstance.getDataURL({
      type: "png",
      pixelRatio: 2,
      backgroundColor: "#fff",
    });

    const link = document.createElement("a");
    link.href = imgData;
    link.download = `${title || "chart"}.png`;
    link.click();
  };

  const handleChartChange = (type) => {
    setChartType(type);
  };

  const getOption = () => {
    const timeLabels = data?.map((item) => item.time);
    const seriesNames = Object.keys(data[0] || {}).filter(
      (key) => key !== "time",
    );
    // /////////////////OPTION /////////////////////////////////////
    let dataZoom;
    const dataLength = data?.length;
    if ((title = "20ACP")) {
      dataZoom = 100;
    } else if (dataLength <= 12) {
      dataZoom = 100;
    } else if (dataLength >= 12 && dataLength <= 36) {
      dataZoom = 50;
    } else if (dataLength >= 36 && dataLength <= 48) {
      dataZoom = 300;
    } else if (dataLength >= 48) {
      dataZoom = 20;
    }

    let gridOption = {
      left: "1%",
      right: "1%",
      bottom: "15%",
      containLabel: true,
    };
    let singleDataGridOption = {
      left: "1%",
      right: "1%",
      top: "30%",
      bottom: "0%",
      containLabel: true,
    };
    // Define custom colors per series
    const lineColors = {
      MEXP: "#42beec", // blue
      PASS: "#ec425b", //
      "Pass Pun": "#b5681aff", // blue
      "Mexp Pun": "#3a7fadff", //
      "Mexp Trains": "#0d9bfaff", // blue
      "Pass Trains": "rgba(198, 145, 91, 1)", // red
      "Working Hours": "#d66d50", // red
      "Coaching Trains": "#4d46d8ff",
      GOODS: "#2ecc71", // green
      SPEED: "#ecba42",
      LDG_WGN: "#ec426aff",
      LOADING: "#4280ec", // dark blue
      DWB: "#c7de1bff",
      lost_count: "#176ba8ef",
      "Lobby wise Working Hours": "#176ba8ef",
      Punctuality: "#3a7fadff",
      LDG_MT: "#fa9d4a", // red
      TotalTrain: "#ff0101ff", // red
      "Month Wise Working Hours": "#7f4a5dff",
    };

    return {
      tooltip: {
        trigger: "axis",
      },
      legend: {
        data: seriesNames,
      },
      xAxis: {
        type: "category",
        data: timeLabels,
        axisLabel: {
          fontSize: 12,
          interval: 0,
          rotate: rotate,
          color: "rgb(0,0,0)",
          // rotate: from === "hours" ? 35 : 0,
        },
      },
      yAxis:
        // chartType === "reset" || seriesNames.includes("LDG_WGN")
        //   ?
        [
          {
            type: "value",
            name: singleValue
              ? null
              : seriesNames[0] === "Punctuality"
                ? seriesNames[0]
                : "Others",
            position: "left",
            axisLabel: {
              interval: 1,
              fontSize: 12,
            },
          },
          {
            type: "value",
            name: singleValue
              ? null
              : seriesNames.includes("LDG_WGN")
                ? "LDG_WGN"
                : seriesNames.includes("SPEED")
                  ? "Speed"
                  : seriesNames.includes("DWB")
                    ? "DWB"
                    : seriesNames.includes("TotalTrain")
                      ? "Total Train"
                      : "Others",
            // min: 0, // Optional: min value
            // max: 30, // Optional: max value
            // interval: 10,
            position: singleValue ? "left" : "right",
            axisLabel: {
              formatter: "{value}",
              fontSize: 12,
            },
            splitLine: {
              show: singleValue ? true : false,
            },
          },
        ],
      // : {
      //     type: "value",
      //     name: "Others",
      //     position: "left",
      //     axisLabel: {
      //       interval: 1,
      //     },
      //   },
      grid: singleValue ? singleDataGridOption : gridOption,
      dataZoom: [
        {
          type: "slider",
          show: singleValue ? false : true, // hiding the scroll bar is there is only one entity
          xAxisIndex: 0,
          start: 0,
          end: dataZoom, // adjust this percentage as needed
        },
        {
          type: "inside",
          xAxisIndex: 0,
          start: 0,
          end: 50, // sync with slider
          height: 15,
        },
      ],
      series: seriesNames.map((name) => {
        let color = lineColors[name];
        const isMix =
          name === "SPEED" ||
          name === "LDG_WGN" ||
          name === "TotalTrain" ||
          name === "Coaching Trains" ||
          name === "Pass Trains" ||
          name === "Mexp Trains" ||
          name === "DWB";
        const leftLine = null;

        return {
          name,
          type:
            chartType === "line"
              ? "line"
              : chartType === "bar"
                ? "bar"
                : isMix || leftLine
                  ? "line"
                  : "bar",

          yAxisIndex: isMix ? 1 : 0, // <-- This assigns right Y-axis to SPEED
          label: {
            show: true,
            position: "top",
            offset: name === "SPEED" || name === "LDG_WGN" ? [0, -10] : [0, 0], // lift lines up a bit
            fontSize: 12,
            color: color,
            fontWeight: "bold",
            formatter: (params) => params.value,
          },
          labelLayout: {
            hideOverlap: true, // hides label if it still overlaps
            adjustPosition: true, // dynamically move labels to avoid overlap
          },

          data: data.map((item) => ({
            value: item[name],
            label: {
              show: true,
              position: "top",
              formatter: item[name.replace("_", " ")],
              color: color,
              fontWeight: "bold",
              fontSize: 12,
              textShadowColor: "rgba(0, 0, 0, 0.3)",
              textShadowBlur: 4,
              textShadowOffsetX: 2,
              textShadowOffsetY: 2,
            },
          })),
          lineStyle: {
            color: color,
            width: 3,
            shadowColor: "rgba(0, 0, 0, 0.4)",
            shadowBlur: 4,
            shadowOffsetX: 4,
            shadowOffsetY: 5,
            borderRadius: [4, 4, 0, 0], // optional, for rounded bars
          },
          itemStyle: {
            color: color,
            shadowColor: "rgba(0, 0, 0, 0.4)",
            shadowBlur: 5,
            shadowOffsetX: 4,
            shadowOffsetY: 1,
            borderRadius: [4, 4, 0, 0], // optional, for rounded bars
          },
          animationDuration: 2000,
          animationEasing: "linear",
          smooth: true,
          markPoint: {
            data: [
              {
                type: "max",
                name: "max",
                label: {
                  formatter: "{b}",
                  color: color,
                  fontWeight: "bold",
                  textShadowColor: "rgba(0, 0, 0, 0.3)",
                  textShadowBlur: 4,
                  textShadowOffsetX: 2,
                  textShadowOffsetY: 2,
                },
                itemStyle: {
                  color: "rgb(0, 0, 0, 0.0)",
                },
              },
              {
                type: "min",
                name: "min",
                label: {
                  formatter: "{b}",
                  color: color,
                  fontWeight: "bold",
                  textShadowColor: "rgba(0, 0, 0, 0.3)",
                  textShadowBlur: 4,
                  textShadowOffsetX: 2,
                  textShadowOffsetY: 2,
                },
                itemStyle: {
                  color: "rgb(0, 0, 0, 0.0)",
                },
              },
            ],
          },
        };
      }),
    };
  };
  const onEvents = {
    click: (params) => {
      const bar = params.name; // category name (e.g. Jan)
      const barValue = params.value; // value (e.g. 120)
      const seriesName = params.seriesName;

      setSelectedLineBar({
        [barName]: bar,
        barValue,
        seriesName,
      });
    },
  };

  return (
    <div style={{ overflow: "hidden" }}>
      {singleValue ? null : (
        <div className="d-flex justify-content-end mt-1 ">
          <button
            className="btn  btn-sm btn-outline-dark d-flex align-item-center me-1"
            style={{ height: "25px" }}
            onClick={downloadChart}
          >
            <FaDownload />
          </button>
          <button
            className={`btn  btn-sm btn-outline-dark d-flex align-item-center me-1 ${
              chartType === "bar" ? "active" : ""
            }`}
            style={{ height: "25px" }}
            onClick={() => {
              handleChartChange("bar");
            }}
          >
            <FaChartBar />
          </button>
          <button
            className={`btn  btn-sm btn-outline-dark d-flex align-item-center me-1 ${
              chartType === "line" ? "active" : ""
            }`}
            style={{ height: "25px" }}
            onClick={() => {
              handleChartChange("line");
            }}
          >
            <FaChartLine />
          </button>
          <button
            className={`btn  btn-sm btn-outline-dark d-flex align-item-center me-1 ${
              chartType === "reset" ? "active" : ""
            }`}
            style={{ height: "25px" }}
            onClick={() => {
              handleChartChange("reset");
            }}
          >
            <RiResetLeftLine />
          </button>
        </div>
      )}
      {data.length === 0 ? "No Data Found" : null}
      <ReactECharts
        ref={chartRef}
        onEvents={onEvents}
        option={getOption()}
        notMerge={true}
        style={{
          height: title === "20ACP" ? "300px" : singleValue ? "120px" : "300px",
          width: "auto",
        }}
      />
    </div>
  );
};

export default LineRaceChart;
